import React, { useCallback, useState } from 'react';

import { Alert, Box, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';

import { useGetBundles } from '~/hooks/apis/accommodation/useBundles';

import { Bundle, BundleHotelRule } from '~/services/AccommodationService';

import BundleDetailDialog from './BundleDetailDialog';
import { BOARD_CODE_LABELS } from './constants';

const PAGE_SIZE = 25;

const columns: Array<GridColDef> = [
  {
    field: 'name',
    headerName: 'Bundle Name',
    flex: 2,
    minWidth: 200,
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 120,
    renderCell: ({ value }) => (
      <Chip label={value} color={value === 'active' ? 'success' : 'default'} size="small" variant="outlined" />
    ),
  },
  {
    field: 'offers',
    headerName: 'Offers',
    width: 100,
    valueGetter: (_value, row) => row.offers?.length ?? 0,
  },
  {
    field: 'rules',
    headerName: 'Rules',
    width: 100,
    valueGetter: (_value, row) => row.rules?.length ?? 0,
  },
  {
    field: 'schedules',
    headerName: 'Schedules',
    width: 100,
    valueGetter: (_value, row) => row.schedules?.length ?? 0,
  },
  {
    field: 'boardCodes',
    headerName: 'Board Codes',
    flex: 1,
    minWidth: 180,
    sortable: false,
    valueGetter: (_value, row) => {
      const allCodes = (row.rules ?? []).flatMap((r: BundleHotelRule) => r.boardCodes);
      return [...new Set(allCodes)];
    },
    renderCell: ({ value }) => (
      <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center" sx={{ py: 0.5 }}>
        {(value as Array<string>).map((code) => (
          <Chip key={code} label={BOARD_CODE_LABELS[code] ?? code} size="small" variant="outlined" />
        ))}
      </Stack>
    ),
  },
];

export default function AccommodationBundlesContainer() {
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: PAGE_SIZE,
  });
  const [selectedBundleId, setSelectedBundleId] = useState<string | null>(null);

  const { data, isLoading, error } = useGetBundles({
    limit: paginationModel.pageSize,
    page: paginationModel.page + 1,
  });

  const handleRowClick = useCallback((params: { row: Bundle }) => {
    setSelectedBundleId(params.row.id);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setSelectedBundleId(null);
  }, []);

  if (error) {
    return (
      <Alert severity="error">
        <Typography>Failed to load bundles: {error.message}</Typography>
      </Alert>
    );
  }

  return (
    <Box>
      {isLoading && (
        <Box p={4} display="flex" justifyContent="center">
          <CircularProgress variant="indeterminate" />
        </Box>
      )}

      {!isLoading && (
        <DataGrid
          rows={data?.bundles ?? []}
          columns={columns}
          rowCount={data?.total ?? 0}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          paginationMode="server"
          pageSizeOptions={[10, 25, 50]}
          onRowClick={handleRowClick}
          autoHeight
          disableRowSelectionOnClick
          sx={{ cursor: 'pointer' }}
        />
      )}

      {selectedBundleId && (
        <BundleDetailDialog bundleId={selectedBundleId} open={!!selectedBundleId} onClose={handleCloseDialog} />
      )}
    </Box>
  );
}
